import React from 'react';
import styled, { CSSObject } from '@emotion/styled';
import FavoriteButtonV2 from '../atoms/buttons/FavoriteButtonV2';
import { getButtonIcons, getTextColor, type } from '../style';
import breakpoints from '../theme/material/breakpoints';
import VerifiedBadgeTooltip from '../components/common/VerifiedBadgeTooltip';
import { FONT_FAMILIES } from 'theme/material/constants';
import { isDarkMode } from 'utils/helpers';

const Details = styled('div')({
  width: '100%',
  [breakpoints.down(620.05)]: {
    padding: '0 15px',
  },
});

const Username = styled('div')(() => ({
  margin: 0,
  ...type.title.MD,
  letterSpacing: -0.3,
  fontWeight: 400,
  lineHeight: 'normal',
  fontFamily: FONT_FAMILIES.secondary,
  color: !isDarkMode() ? '#212121' : '#FFFFFF',
  [breakpoints.down(620.05)]: {
    color: '#fff',
    ...type.title.XS,
    letterSpacing: -0.5,
  },
}));

const VerifiedBadgeWrapper = styled('div')({
  display: 'inline-block',
  marginLeft: 4,
  position: 'relative',
  top: 6,
  [breakpoints.down(768.05)]: {
    top: 4,
  },
});

const Detail = styled('p')(() => ({
  ...type.MD,
  lineHeight: '21px',
  letterSpacing: -0.3,
  ...getTextColor().DARK_2,
  margin: 0,
  textOverflow: 'ellipsis',
  overflow: 'hidden',
  width: 350,
  whiteSpace: 'nowrap',
  [breakpoints.down(620.05)]: {
    ...type.XS,
    lineHeight: 'normal',
    letterSpacing: 'normal',
    color: '#AFB0B1',
    paddingBottom: 3,
    width: '100%',
    whiteSpace: 'unset',
    overflow: 'unset',
  },
  [breakpoints.between(621, 747)]: {
    width: '100%',
  },
}));

const DetailLastActive = styled('p')(() => ({
  ...type.XS,
  lineHeight: '15px',
  letterSpacing: -0.3,
  margin: 0,
  [breakpoints.down(620.05)]: {
    fontSize: 11,
    lineHeight: '19px',
    color: '#AFB0B1',
  },
}));

const StyledFavoriteButton = styled(FavoriteButtonV2)(
  (): CSSObject => ({
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    svg: {
      [breakpoints.down(620.05)]: {
        height: '28.21px',
      },
    },
    '&.unFavoriteStyle::before': {
      content: "''",
      position: 'absolute',
      borderBottom: getButtonIcons().borderStyle,
      transform: 'translateY(25px) translateX(10px) rotate(318deg)',
      top: '-49px',
      left: '-30px',
      width: '54px',
      height: '54px',
      zIndex: 1,

      [breakpoints.down(768.05)]: {
        top: '-43px',
        left: '-24px',
        width: '40px',
        height: '40px',
        borderBottomWidth: '2px',
      },
      [breakpoints.down(639.05)]: {
        top: '-43px',
        left: '-24px',
        width: '40px',
        height: '40px',
        borderBottomWidth: '2px',
      },
      '@media (max-width: 550px)': {
        top: '-43px',
        left: '-26px',
        width: '40px',
        height: '40px',
        borderBottomWidth: '2px',
      },
    },
    '+ label': {
      marginTop: 0,
      display: 'none',
    },
  })
);

const FavButtonWrapper = styled('div')({
  [breakpoints.down(620.05)]: {
    paddingRight: 15,
  },
});

const OtherProfileCoverInfo = ({
  username,
  auth10tixVerification,
  isVerified,
  age,
  location,
  lastLoginTime,
  lastLoginLocation,
  favorited,
  onClickFavorite,
  shouldShowFavoriteButton,
  isSelfieVerified,
}: any): any => {
  return (
    <>
      <Details>
        <Username data-test-id="profile-username-info">
          {username}
          {((auth10tixVerification && isVerified) || isSelfieVerified) && (
            <VerifiedBadgeWrapper id="badge-tooltip">
              <VerifiedBadgeTooltip showToolTip={true} isSelfieVerified={isSelfieVerified} isIdVerified={isVerified} />
            </VerifiedBadgeWrapper>
          )}
        </Username>
        <Detail data-test-id="profile-user-details">{`${age} • ${location}`}</Detail>
        <DetailLastActive data-test-id="profile-last-log">
          {lastLoginTime} {lastLoginLocation && `in ${lastLoginLocation}`}
        </DetailLastActive>
      </Details>
      {shouldShowFavoriteButton && (
        <FavButtonWrapper>
          <StyledFavoriteButton
            data-test-id="profile-favorite-button"
            favorited={favorited}
            onClick={onClickFavorite}
            className={favorited ? 'unFavoriteStyle' : 'favoriteStyle'}
          />
        </FavButtonWrapper>
      )}
    </>
  );
};

export default React.memo(OtherProfileCoverInfo);
